// Phone-side port of `src/face_engine.cpp` + `src/face_renderer.cpp`.
//
// Goal: render the 17 FaceState variants on a 128x64 canvas with the same
// visual language as the OLED. Not pixel-perfect (Chunk 2 introduces parity
// tests against C++ golden frames); aims for "instantly recognizable as Mochi"
// on first glance.
//
// Coordinate system: 0..127 X, 0..63 Y. Origin top-left, +Y down.

import { PalForm, PalMark } from './types';

// ── FaceState enum: ORDER MUST MATCH src/car_state.h exactly. ─────────────
// (It's used as a numeric index for the demo-state HTTP endpoint and any
// future cross-platform tests, so renumbering is a breaking change.)
export enum FaceState {
    SLEEPING       = 0,
    IDLE           = 1,
    CRUISING       = 2,
    EXCITED        = 3,
    SCARED         = 4,
    NERVOUS        = 5,
    INTENSE        = 6,
    LOOKING_LEFT   = 7,
    LOOKING_RIGHT  = 8,
    COLD           = 9,
    OVERHEATING    = 10,
    LOW_FUEL       = 11,
    CHECK_ENGINE   = 12,
    DISCONNECTED   = 13,
    HAPPY_CRUISE   = 14,
    HUNGRY         = 15,
    SUPER_EXCITED  = 16,
}

export const FACE_STATE_NAMES: Record<FaceState, string> = {
    [FaceState.SLEEPING]:      'SLEEPING',
    [FaceState.IDLE]:          'IDLE',
    [FaceState.CRUISING]:      'CRUISING',
    [FaceState.EXCITED]:       'EXCITED',
    [FaceState.SCARED]:        'SCARED',
    [FaceState.NERVOUS]:       'NERVOUS',
    [FaceState.INTENSE]:       'INTENSE',
    [FaceState.LOOKING_LEFT]:  'LOOKING_LEFT',
    [FaceState.LOOKING_RIGHT]: 'LOOKING_RIGHT',
    [FaceState.COLD]:          'COLD',
    [FaceState.OVERHEATING]:   'OVERHEATING',
    [FaceState.LOW_FUEL]:      'LOW_FUEL',
    [FaceState.CHECK_ENGINE]:  'CHECK_ENGINE',
    [FaceState.DISCONNECTED]:  'DISCONNECTED',
    [FaceState.HAPPY_CRUISE]:  'HAPPY_CRUISE',
    [FaceState.HUNGRY]:        'HUNGRY',
    [FaceState.SUPER_EXCITED]: 'SUPER_EXCITED',
};

// ── RenderStyle enum: mirror of `src/face_engine.h`. ─────────────────────
export enum RenderStyle {
    MOCHI,            // dot eyes + small mouth
    NORMAL,
    HALF_OPEN,
    CLOSED,
    X_EYES,           // CHECK_ENGINE
    SPIRAL_EYES,      // OVERHEATING
    DOLLAR_EYES,      // HUNGRY
    STAR_EYES,        // SUPER_EXCITED
    SMILE_FACE,       // HAPPY_CRUISE
    ZZZ_SLEEPY,       // SLEEPING
    SNOWFLAKE_COLD,   // COLD
    EXCLAIM_SCARED,   // SCARED
}

export interface ExpressionParams {
    pupilY:        number;   // -2 .. +2 (px offset from centre)
    pupilX:        number;   // -2 .. +2
    eyebrowsLow:   number;   // 0..1 (squint)
    pupilSize:     number;   // 0..1 (eye dot size)
    mouthCurve:    number;   // -7..+10 (frown..smile)
    drawOpen:      boolean;
    drawClosed:    boolean;
    style:         RenderStyle;
}

// Mirror of `STATE_PARAMS[]` in src/face_engine.cpp.
export const STATE_PARAMS: Record<FaceState, ExpressionParams> = {
    [FaceState.SLEEPING]:      { pupilY: 0,    pupilX: 0,     eyebrowsLow: 1.0, pupilSize: 0.40, mouthCurve: 0,   drawOpen: false, drawClosed: true,  style: RenderStyle.ZZZ_SLEEPY },
    [FaceState.IDLE]:          { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0.4, pupilSize: 0.45, mouthCurve: 2,   drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.CRUISING]:      { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0.1, pupilSize: 0.55, mouthCurve: 1,   drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.EXCITED]:       { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0,   pupilSize: 0.95, mouthCurve: 8,   drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.SCARED]:        { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0,   pupilSize: 0.95, mouthCurve: -2,  drawOpen: true,  drawClosed: false, style: RenderStyle.EXCLAIM_SCARED },
    [FaceState.NERVOUS]:       { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0.1, pupilSize: 0.50, mouthCurve: -3,  drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.INTENSE]:       { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0.6, pupilSize: 0.60, mouthCurve: -5,  drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.LOOKING_LEFT]:  { pupilY: 0,    pupilX: -1.0,  eyebrowsLow: 0.1, pupilSize: 0.55, mouthCurve: 1,   drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.LOOKING_RIGHT]: { pupilY: 0,    pupilX: 1.0,   eyebrowsLow: 0.1, pupilSize: 0.55, mouthCurve: 1,   drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.COLD]:          { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0.3, pupilSize: 0.45, mouthCurve: -2,  drawOpen: true,  drawClosed: false, style: RenderStyle.SNOWFLAKE_COLD },
    [FaceState.OVERHEATING]:   { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0,   pupilSize: 0.60, mouthCurve: 0,   drawOpen: true,  drawClosed: false, style: RenderStyle.SPIRAL_EYES },
    [FaceState.LOW_FUEL]:      { pupilY: 0.3,  pupilX: 0,     eyebrowsLow: 0.5, pupilSize: 0.40, mouthCurve: -7,  drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.CHECK_ENGINE]:  { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0,   pupilSize: 0.30, mouthCurve: 0,   drawOpen: false, drawClosed: true,  style: RenderStyle.X_EYES },
    [FaceState.DISCONNECTED]:  { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0.3, pupilSize: 0.40, mouthCurve: 0,   drawOpen: true,  drawClosed: false, style: RenderStyle.MOCHI },
    [FaceState.HAPPY_CRUISE]:  { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0.7, pupilSize: 0.50, mouthCurve: 10,  drawOpen: true,  drawClosed: false, style: RenderStyle.SMILE_FACE },
    [FaceState.HUNGRY]:        { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0,   pupilSize: 0.50, mouthCurve: 4,   drawOpen: true,  drawClosed: false, style: RenderStyle.DOLLAR_EYES },
    [FaceState.SUPER_EXCITED]: { pupilY: 0,    pupilX: 0,     eyebrowsLow: 0,   pupilSize: 0.50, mouthCurve: 10,  drawOpen: true,  drawClosed: false, style: RenderStyle.STAR_EYES },
};

// Constants matching the OLED layout.
const W = 128;
const H = 64;
const EYE_LEFT_CX  = 44;
const EYE_RIGHT_CX = 84;
const EYE_CY       = 28;
const MOUTH_CX     = 64;
const MOUTH_CY     = 50;

// Accessory IDs (placeholder set for Chunk 1; expanded in Chunk 4).
export enum AccessoryId {
    None        = 0,
    FirstHelmet = 1,
    Sunglasses  = 2,
    Crown       = 3,
    Cape        = 4,
}

export interface RenderInput {
    state:       FaceState;
    form:        PalForm;
    marks:       number;
    accessory:   AccessoryId;
    nowMs:       number;
    blink?:      boolean;        // ignored by SLEEPING/CHECK_ENGINE
}

export function renderFace(ctx: CanvasRenderingContext2D, input: RenderInput): void {
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#fff';

    const p = STATE_PARAMS[input.state];
    drawByStyle(ctx, p, input);

    // Form decoration overlay (Calm Explorer halo, Night Cruiser stars, Spirit fire).
    drawFormDecoration(ctx, input.form, input.nowMs);

    // Mark badges
    if (input.marks & PalMark.SmoothDriveWeek)    drawSparkle(ctx, 4, 4, input.nowMs);
    if (input.marks & PalMark.NightCruiserStreak) drawMoon(ctx, W - 12, 4);

    // Accessory overlay last so it sits on top
    if (input.accessory !== AccessoryId.None) {
        drawAccessory(ctx, input.accessory);
    }
}

// ── Style dispatcher ─────────────────────────────────────────────────────
function drawByStyle(ctx: CanvasRenderingContext2D, p: ExpressionParams, input: RenderInput) {
    switch (p.style) {
        case RenderStyle.MOCHI:          drawMochi(ctx, p, input); break;
        case RenderStyle.X_EYES:         drawXEyes(ctx); drawMouth(ctx, p.mouthCurve); break;
        case RenderStyle.SPIRAL_EYES:    drawSpiralEyes(ctx, input.nowMs); drawMouth(ctx, p.mouthCurve); break;
        case RenderStyle.DOLLAR_EYES:    drawDollarEyes(ctx); drawMouth(ctx, p.mouthCurve); break;
        case RenderStyle.STAR_EYES:      drawStarEyes(ctx); drawMouth(ctx, p.mouthCurve); break;
        case RenderStyle.SMILE_FACE:     drawHappyEyes(ctx); drawMouth(ctx, p.mouthCurve); break;
        case RenderStyle.ZZZ_SLEEPY:     drawClosedEyes(ctx); drawZ(ctx, input.nowMs); break;
        case RenderStyle.SNOWFLAKE_COLD: drawMochi(ctx, p, input); drawSnowflakes(ctx, input.nowMs); break;
        case RenderStyle.EXCLAIM_SCARED: drawMochi(ctx, p, input); drawExclamations(ctx, input.nowMs); break;
        default:                         drawMochi(ctx, p, input);
    }
}

// ── MOCHI: parametric dot eyes + curved mouth ───────────────────────────
function drawMochi(ctx: CanvasRenderingContext2D, p: ExpressionParams, input: RenderInput) {
    const radius = 3 + Math.round(p.pupilSize * 4);     // 3..7
    const eyeY = EYE_CY + Math.round(p.pupilY * 3);

    if (p.eyebrowsLow > 0.85 || (input.blink && p.drawOpen)) {
        // closed-eyes bars
        bar(ctx, EYE_LEFT_CX - radius, eyeY, radius * 2 + 1, 1);
        bar(ctx, EYE_RIGHT_CX - radius, eyeY, radius * 2 + 1, 1);
    } else {
        const squintTop = Math.round(p.eyebrowsLow * radius);
        eye(ctx, EYE_LEFT_CX  + Math.round(p.pupilX * 2), eyeY, radius, squintTop);
        eye(ctx, EYE_RIGHT_CX + Math.round(p.pupilX * 2), eyeY, radius, squintTop);
    }

    drawMouth(ctx, p.mouthCurve);
}

function eye(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, squintTop: number) {
    fillCircle(ctx, cx, cy, r);
    if (squintTop > 0) {
        ctx.fillStyle = '#000';
        ctx.fillRect(cx - r, cy - r, r * 2 + 1, squintTop);
        ctx.fillStyle = '#fff';
    }
}

function drawMouth(ctx: CanvasRenderingContext2D, curve: number) {
    const half = 8;
    const peak = Math.round(curve);
    if (peak === 0) {
        bar(ctx, MOUTH_CX - half, MOUTH_CY, half * 2 + 1, 1);
        return;
    }
    // simple parabola: y = peak * (1 - (x/half)^2)
    for (let dx = -half; dx <= half; dx++) {
        const t = dx / half;
        const y = MOUTH_CY - Math.round(peak * (1 - t * t));
        if (y >= 0 && y < H) ctx.fillRect(MOUTH_CX + dx, y, 1, 1);
    }
}

function drawClosedEyes(ctx: CanvasRenderingContext2D) {
    bar(ctx, EYE_LEFT_CX - 6, EYE_CY, 13, 1);
    bar(ctx, EYE_RIGHT_CX - 6, EYE_CY, 13, 1);
}

function drawHappyEyes(ctx: CanvasRenderingContext2D) {
    // upward-curving "^" shapes
    [EYE_LEFT_CX, EYE_RIGHT_CX].forEach(cx => {
        for (let dx = -5; dx <= 5; dx++) {
            const y = EYE_CY - Math.round(3 * (1 - (dx / 5) * (dx / 5)));
            ctx.fillRect(cx + dx, y, 1, 1);
        }
    });
}

function drawXEyes(ctx: CanvasRenderingContext2D) {
    [EYE_LEFT_CX, EYE_RIGHT_CX].forEach(cx => {
        for (let i = -4; i <= 4; i++) {
            ctx.fillRect(cx + i, EYE_CY + i, 1, 1);
            ctx.fillRect(cx + i, EYE_CY - i, 1, 1);
        }
    });
}

function drawSpiralEyes(ctx: CanvasRenderingContext2D, t: number) {
    [EYE_LEFT_CX, EYE_RIGHT_CX].forEach((cx, i) => {
        const phase = (t / 200) + i * Math.PI;
        for (let r = 0; r < 6; r += 0.4) {
            const a = phase + r * 1.4;
            ctx.fillRect(
                cx + Math.round(Math.cos(a) * r),
                EYE_CY + Math.round(Math.sin(a) * r),
                1, 1,
            );
        }
    });
}

function drawDollarEyes(ctx: CanvasRenderingContext2D) {
    [EYE_LEFT_CX, EYE_RIGHT_CX].forEach(cx => {
        bar(ctx, cx, EYE_CY - 5, 1, 11);
        for (let dy = -3; dy <= 3; dy++) {
            ctx.fillRect(cx - 3, EYE_CY + dy, 7, 1);
        }
    });
}

function drawStarEyes(ctx: CanvasRenderingContext2D) {
    [EYE_LEFT_CX, EYE_RIGHT_CX].forEach(cx => {
        // 4-point star
        bar(ctx, cx,     EYE_CY - 5, 1, 11);
        bar(ctx, cx - 5, EYE_CY,     11, 1);
        ctx.fillRect(cx - 3, EYE_CY - 3, 1, 1);
        ctx.fillRect(cx + 3, EYE_CY - 3, 1, 1);
        ctx.fillRect(cx - 3, EYE_CY + 3, 1, 1);
        ctx.fillRect(cx + 3, EYE_CY + 3, 1, 1);
    });
}

function drawZ(ctx: CanvasRenderingContext2D, t: number) {
    const float = Math.round(Math.sin(t / 400) * 2);
    const baseX = 92, baseY = 14 + float;
    bar(ctx, baseX, baseY, 6, 1);
    ctx.fillRect(baseX + 5, baseY + 1, 1, 1);
    ctx.fillRect(baseX + 4, baseY + 2, 1, 1);
    ctx.fillRect(baseX + 3, baseY + 3, 1, 1);
    ctx.fillRect(baseX + 2, baseY + 4, 1, 1);
    bar(ctx, baseX, baseY + 5, 6, 1);
}

function drawSnowflakes(ctx: CanvasRenderingContext2D, t: number) {
    const phase = (t / 600) % 1;
    [
        { cx: 10,  cy: 8 },
        { cx: 118, cy: 8 },
        { cx: 10,  cy: 56 },
        { cx: 118, cy: 56 },
    ].forEach((s, i) => {
        const dy = ((phase + i * 0.25) * 4 | 0);
        const cy = s.cy + dy;
        ctx.fillRect(s.cx,     cy - 1, 1, 3);
        ctx.fillRect(s.cx - 1, cy,     3, 1);
    });
}

function drawExclamations(ctx: CanvasRenderingContext2D, t: number) {
    const flash = (Math.floor(t / 250) % 2) === 0;
    if (!flash) return;
    [12, 110].forEach(x => {
        bar(ctx, x, 8, 1, 6);
        ctx.fillRect(x, 16, 1, 1);
    });
}

// ── Form decorations ─────────────────────────────────────────────────────
function drawFormDecoration(ctx: CanvasRenderingContext2D, form: PalForm, t: number) {
    switch (form) {
        case PalForm.CalmExplorer:
            // gentle halo arc above face
            for (let dx = -16; dx <= 16; dx++) {
                const y = 6 - Math.round(2 * (1 - (dx / 16) ** 2));
                if (y >= 0) ctx.fillRect(MOUTH_CX + dx, y, 1, 1);
            }
            break;
        case PalForm.NightCruiser: {
            // 3 twinkling stars
            const stars = [{ x: 10, y: 6 }, { x: 64, y: 4 }, { x: 116, y: 8 }];
            stars.forEach((s, i) => {
                if (((Math.floor(t / 400) + i) % 3) === 0) {
                    ctx.fillRect(s.x, s.y - 1, 1, 3);
                    ctx.fillRect(s.x - 1, s.y, 3, 1);
                }
            });
            break;
        }
        case PalForm.SpiritDriver: {
            // flame flicker along bottom edge
            for (let x = 0; x < W; x += 4) {
                const flicker = Math.round(Math.sin((t / 80) + x) * 1.5) + 2;
                bar(ctx, x, H - 1 - flicker, 2, flicker);
            }
            break;
        }
        case PalForm.Baby:
        default:
            break;
    }
}

// ── Mark badges ──────────────────────────────────────────────────────────
function drawSparkle(ctx: CanvasRenderingContext2D, cx: number, cy: number, t: number) {
    const phase = (Math.floor(t / 300) % 2) === 0;
    if (!phase) return;
    ctx.fillRect(cx, cy - 2, 1, 5);
    ctx.fillRect(cx - 2, cy, 5, 1);
}

function drawMoon(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
    fillCircle(ctx, cx, cy, 3);
    ctx.fillStyle = '#000';
    fillCircle(ctx, cx + 2, cy - 1, 2);
    ctx.fillStyle = '#fff';
}

// ── Accessories (Chunk 1: simple hat overlays) ───────────────────────────
function drawAccessory(ctx: CanvasRenderingContext2D, id: AccessoryId) {
    switch (id) {
        case AccessoryId.FirstHelmet:
            // dome above face
            for (let dx = -22; dx <= 22; dx++) {
                const y = 12 - Math.round(8 * Math.sqrt(Math.max(0, 1 - (dx / 22) ** 2)));
                bar(ctx, MOUTH_CX + dx, y, 1, 12 - y);
            }
            break;
        case AccessoryId.Sunglasses:
            ctx.fillStyle = '#000';
            ctx.fillRect(EYE_LEFT_CX  - 8, EYE_CY - 5, 16, 10);
            ctx.fillRect(EYE_RIGHT_CX - 8, EYE_CY - 5, 16, 10);
            ctx.fillStyle = '#fff';
            bar(ctx, EYE_LEFT_CX + 8, EYE_CY, 24, 1);  // bridge
            break;
        case AccessoryId.Crown:
            // 3 spikes above head
            [44, 64, 84].forEach(x => {
                ctx.fillRect(x, 4, 1, 8);
                ctx.fillRect(x - 1, 11, 3, 1);
            });
            bar(ctx, 40, 12, 49, 1);
            break;
        case AccessoryId.Cape:
            // shoulders + cape
            for (let y = 56; y < H; y++) {
                bar(ctx, 8, y, W - 16, 1);
            }
            break;
        case AccessoryId.None:
        default: break;
    }
}

// ── Primitives ───────────────────────────────────────────────────────────
function bar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
    ctx.fillRect(x, y, w, h);
}

function fillCircle(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
    for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
            if (dx * dx + dy * dy <= r * r) ctx.fillRect(cx + dx, cy + dy, 1, 1);
        }
    }
}

export const FACE_W = W;
export const FACE_H = H;
